import React, { useState, useEffect } from 'react';
import { useSiteData } from '@/context/SiteDataContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { siteSettings } = useSiteData();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Submit to backend
      await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
    } catch (err) {
      console.error('Error submitting contact form:', err);
    }

    // Create WhatsApp message
    const message = `*New Contact Inquiry*%0A%0A` +
      `*Name:* ${formData.firstName} ${formData.lastName}%0A` +
      `*Phone:* ${formData.phone}%0A` +
      `*Email:* ${formData.email}%0A%0A` +
      `Please get in touch with me regarding travel packages.`;

    const phoneNumber = siteSettings.whatsappNumber.replace('+', '');
    
    // Open WhatsApp
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');

    setIsSubmitted(true);
    
    setTimeout(() => {
      onClose();
      setTimeout(() => {
        setFormData({ firstName: '', lastName: '', phone: '', email: '' });
        setIsSubmitted(false);
      }, 300);
    }, 1000);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="contact-modal-overlay"
      onClick={handleBackdropClick}
      data-testid="contact-modal-overlay"
    >
      <div className="contact-modal-container" data-testid="contact-modal">
        {/* Header Section */}
        <div className="contact-modal-header">
          <button 
            onClick={onClose} 
            className="contact-modal-close-btn"
            data-testid="contact-modal-close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
          <h3 className="contact-modal-title">Get In Touch With Us</h3>
          <p className="contact-modal-subtitle">Have questions? We're here to help plan your perfect vacation</p>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="contact-modal-form" data-testid="contact-modal-form">
          <div className="contact-form-row">
            <div className="contact-form-field">
              <label className="contact-form-label">
                First Name <span className="required-star">*</span>
              </label>
              <input 
                type="text" 
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First Name" 
                required 
                className="contact-form-input"
                data-testid="contact-firstname"
              />
            </div>
            <div className="contact-form-field">
              <label className="contact-form-label">
                Last Name <span className="required-star">*</span>
              </label>
              <input 
                type="text" 
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last Name" 
                required 
                className="contact-form-input"
                data-testid="contact-lastname"
              />
            </div>
          </div>
          
          <div className="contact-form-field">
            <label className="contact-form-label">
              Phone <span className="required-star">*</span>
            </label>
            <input 
              type="tel" 
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone Number" 
              required 
              className="contact-form-input"
              data-testid="contact-phone"
            />
          </div>
          
          <div className="contact-form-field">
            <label className="contact-form-label">
              Email <span className="required-star">*</span>
            </label>
            <input 
              type="email" 
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address" 
              required 
              className="contact-form-input"
              data-testid="contact-email"
            />
          </div>
          
          <button 
            type="submit" 
            className={`contact-submit-btn ${isSubmitted ? 'submitted' : ''}`}
            data-testid="contact-submit-btn"
          >
            {isSubmitted ? (
              <>
                <i className="fa-solid fa-circle-check"></i>
                Sent Successfully!
              </>
            ) : (
              'Send Message'
            )}
          </button>

          {/* Close Button */}
          <button 
            type="button"
            onClick={onClose}
            className="contact-close-btn-bottom"
            data-testid="contact-close-btn"
          >
            Close
          </button>

          {/* Footer Info */}
          <div className="contact-modal-footer">
            <p>
              You can email us at <a href={`mailto:${siteSettings.email}`} className="contact-email-link">{siteSettings.email}</a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
